
/**
 * Utility functions for working with SoundCloud links
 */

/**
 * Check if a URL is a SoundCloud link
 */
export const isSoundCloudLink = (url: string): boolean => {
  if (!url) return false;
  
  try {
    // Normalize the URL to handle various SoundCloud URL formats
    const normalizedUrl = url.toLowerCase().trim();
    
    // Check for common SoundCloud URL patterns
    const isSoundCloud = normalizedUrl.includes('soundcloud.com/');
    
    // For YouTube-like IDs that don't contain soundcloud.com
    // Consider them valid SoundCloud links for our placeholder approach
    if (!isSoundCloud && (normalizedUrl.length === 11 || normalizedUrl.includes('youtu'))) {
      console.log(`[soundcloudUtils] Converting YouTube-like URL ${url} to placeholder SoundCloud URL`);
      return true;
    }
    
    console.log(`[soundcloudUtils] URL ${url} is a SoundCloud link: ${isSoundCloud}`);
    return isSoundCloud;
  } catch (e) {
    console.error("[soundcloudUtils] Error checking SoundCloud link:", e);
    return false;
  }
};

/**
 * Extract track URL from a SoundCloud URL
 * This just returns the full URL as SoundCloud embeds use the full URL
 */
export const getSoundCloudTrackUrl = (url: string): string | null => {
  if (!url) return null;
  
  try {
    // For SoundCloud, we just need to ensure it's a valid URL
    if (isSoundCloudLink(url)) {
      const normalizedUrl = url.trim();
      
      // If it's a YouTube-like URL, convert it to a placeholder SoundCloud URL
      if (!normalizedUrl.includes('soundcloud.com/')) {
        const placeholderUrl = `https://soundcloud.com/placeholder/${normalizedUrl.replace(/[^a-zA-Z0-9_-]/g, '')}`;
        console.log("[soundcloudUtils] Created placeholder SoundCloud URL:", placeholderUrl);
        return placeholderUrl;
      }
      
      console.log("[soundcloudUtils] Extracted SoundCloud URL:", normalizedUrl);
      // Return the normalized URL
      return normalizedUrl;
    }
    
    return null;
  } catch (e) {
    console.error("[soundcloudUtils] Error extracting SoundCloud track URL:", e);
    return null;
  }
};

/**
 * Check if a SoundCloud URL is valid
 */
export const isValidSoundCloudUrl = (url: string | null): boolean => {
  if (!url) return false;
  
  // Any URL with soundcloud.com is considered valid
  const isValid = url.includes('soundcloud.com/');
  console.log("[soundcloudUtils] SoundCloud URL validity check:", url, isValid);
  return isValid;
};

/**
 * Generate a SoundCloud embed URL from a track URL
 */
export const getSoundCloudEmbedUrl = (trackUrl: string): string => {
  console.log("[soundcloudUtils] Generating SoundCloud embed URL for:", trackUrl);
  
  // For YouTube-like URLs that don't contain soundcloud.com, create a placeholder
  let processedUrl = trackUrl;
  if (!trackUrl.includes('soundcloud.com/')) {
    processedUrl = `https://soundcloud.com/placeholder/${trackUrl.replace(/[^a-zA-Z0-9_-]/g, '')}`;
    console.log("[soundcloudUtils] Converted to placeholder URL:", processedUrl);
  }
  
  // Construct the embed URL with auto_play=false and visual=true for a nice player
  // Hide related sounds and comments to keep it clean
  // For direct SoundCloud URLs, we need to make sure they're correctly formatted
  const embedUrl = `https://w.soundcloud.com/player/?url=${encodeURIComponent(processedUrl)}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=false`;
  console.log("[soundcloudUtils] Generated embed URL:", embedUrl);
  return embedUrl;
};

/**
 * Log information about a SoundCloud URL to aid debugging
 */
export const logSoundCloudUrlInfo = (url: string | null, context: string): void => {
  if (!url) {
    console.log(`[${context}] No SoundCloud URL provided`);
    return;
  }
  
  const isValid = isValidSoundCloudUrl(url);
  console.log(`[${context}] SoundCloud URL: ${url}`);
  console.log(`[${context}] Is valid format: ${isValid}`);
  
  if (isValid) {
    const embedUrl = getSoundCloudEmbedUrl(url);
    console.log(`[${context}] Generated embed URL: ${embedUrl}`);
  }
};
